<!-- Search trigger button -->
<button
    mat-icon-button
    #searchOrigin
    (click)="openPanel()"
    [matTooltip]="'Buscar'"
    aria-label="Abrir buscador global"
>
    <mat-icon [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
</button>

<!-- Search panel template -->
<ng-template #searchPanel>
    <div
        class="navbar-search-panel bg-card flex flex-col overflow-hidden rounded-xl shadow-2xl"
        (keydown)="onKeydown($event)"
    >
        <!-- Input row -->
        <div class="flex flex-0 items-center gap-2 border-b px-4 py-3">
            <mat-icon
                class="text-secondary icon-size-5"
                [svgIcon]="'heroicons_outline:magnifying-glass'"
            ></mat-icon>
            <input
                #searchInput
                type="text"
                class="min-w-0 flex-auto bg-transparent text-base outline-none"
                placeholder="Buscar clientes, paquetes, facturas, prealertas..."
                autocomplete="off"
                spellcheck="false"
                maxlength="100"
                [formControl]="searchControl"
            />
            @if (loading) {
                <mat-progress-spinner
                    [diameter]="18"
                    [mode]="'indeterminate'"
                ></mat-progress-spinner>
            }
            <button mat-icon-button (click)="closePanel()" aria-label="Cerrar">
                <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
            </button>
        </div>

        <!-- Body -->
        <div class="flex flex-auto flex-col overflow-y-auto">
            @if (errorMessage) {
                <div class="text-secondary px-4 py-6 text-center text-sm">
                    {{ errorMessage }}
                </div>
            } @else if (
                !searchControl.value || searchControl.value.toString().trim().length < 2
            ) {
                <div class="text-secondary px-4 py-6 text-center text-sm">
                    Escribe al menos 2 caracteres para buscar.
                </div>
            } @else if (loading && !results) {
                <div class="text-secondary px-4 py-6 text-center text-sm">Buscando…</div>
            } @else if (results && !hasAnyResults()) {
                <div class="text-secondary px-4 py-6 text-center text-sm">
                    No se encontraron resultados.
                </div>
            } @else if (results) {
                @for (group of groups; track trackByKey($index, group)) {
                    @if (results[group.key]?.items?.length) {
                        <div class="flex flex-col">
                            <div
                                class="text-secondary bg-default sticky top-0 z-10 flex items-center justify-between border-b px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                            >
                                <span>{{ group.label }}</span>
                                <span class="text-hint font-normal normal-case">
                                    {{ results[group.key].items.length }} de
                                    {{ results[group.key].total }}
                                </span>
                            </div>

                            @switch (group.key) {
                                @case ('clients') {
                                    @for (
                                        item of results.clients.items;
                                        track trackById($index, item)
                                    ) {
                                        <a
                                            class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                            [routerLink]="['/admin/clients', item.id]"
                                            (click)="closePanel()"
                                        >
                                            <span class="font-medium">
                                                {{ item.name }} {{ item.surnames }}
                                                <span class="ml-1 text-amber-600">
                                                    # Casillero {{ item.id }}
                                                </span>
                                            </span>
                                            <span class="text-secondary text-xs">
                                                {{ item.email }}
                                                @if (item.identification) {
                                                    · {{ item.identification }}
                                                }
                                            </span>
                                        </a>
                                    }
                                }
                                @case ('package_consolidations') {
                                    @for (
                                        cg of groupedConsolidations;
                                        track trackByClientId($index, cg)
                                    ) {
                                        <div
                                            class="bg-default text-amber-600 px-4 py-1 text-xs font-semibold"
                                        >
                                            # Casillero {{ cg.clientId }}
                                        </div>
                                        @for (
                                            item of cg.items;
                                            track trackById($index, item)
                                        ) {
                                            <a
                                                class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                                [routerLink]="['/admin/consolidations', item.id]"
                                                (click)="closePanel()"
                                            >
                                                <span class="font-medium">
                                                    Consolidado #{{ item.id }}
                                                </span>
                                                <span class="text-secondary text-xs">
                                                    Factura
                                                    {{ item.bill_id ? '#' + item.bill_id : '—' }}
                                                    · {{ item.billed ? 'Facturada' : 'Sin facturar' }}
                                                </span>
                                            </a>
                                        }
                                    }
                                }
                                @case ('packages') {
                                    @for (
                                        cg of groupedPackages;
                                        track trackByClientId($index, cg)
                                    ) {
                                        <div
                                            class="bg-default text-amber-600 px-4 py-1 text-xs font-semibold"
                                        >
                                            # Casillero {{ cg.clientId }}
                                        </div>
                                        @for (
                                            item of cg.items;
                                            track trackById($index, item)
                                        ) {
                                            <a
                                                class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                                [routerLink]="['/admin/packages', item.id]"
                                                (click)="closePanel()"
                                            >
                                                <span class="font-medium">{{ item.tracking }}</span>
                                                <span class="text-secondary text-xs">
                                                    {{ item.status }} · {{ item.provider }}
                                                </span>
                                            </a>
                                        }
                                    }
                                }
                                @case ('bills') {
                                    @for (
                                        cg of groupedBills;
                                        track trackByClientId($index, cg)
                                    ) {
                                        <div
                                            class="bg-default text-amber-600 px-4 py-1 text-xs font-semibold"
                                        >
                                            # Casillero {{ cg.clientId }}
                                        </div>
                                        @for (
                                            item of cg.items;
                                            track trackById($index, item)
                                        ) {
                                            <a
                                                class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                                [routerLink]="['/admin/invoices', item.id]"
                                                (click)="closePanel()"
                                            >
                                                <span class="font-medium">
                                                    Factura #{{ item.id }}
                                                </span>
                                                <span class="text-secondary text-xs">
                                                    {{ item.client_name }} {{ item.client_surnames }}
                                                    · {{ item.status }} ·
                                                    {{ item.total | currency: 'USD' }}
                                                </span>
                                            </a>
                                        }
                                    }
                                }
                                @case ('prealerts') {
                                    @for (
                                        cg of groupedPrealerts;
                                        track trackByClientId($index, cg)
                                    ) {
                                        <div
                                            class="bg-default text-amber-600 px-4 py-1 text-xs font-semibold"
                                        >
                                            # Casillero {{ cg.clientId }}
                                        </div>
                                        @for (
                                            item of cg.items;
                                            track trackById($index, item)
                                        ) {
                                            <a
                                                class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                                [routerLink]="['/admin/prealerts', item.id]"
                                                (click)="closePanel()"
                                            >
                                                <span class="font-medium">{{ item.tracking }}</span>
                                                <span class="text-secondary text-xs">
                                                    {{ item.provider }} · {{ item.type }}
                                                </span>
                                            </a>
                                        }
                                    }
                                }
                                @case ('plan_requests') {
                                    @for (
                                        item of results.plan_requests.items;
                                        track trackById($index, item)
                                    ) {
                                        <a
                                            class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                            [routerLink]="['/admin/plan-requests', item.id]"
                                            (click)="closePanel()"
                                        >
                                            <span class="font-medium">
                                                Solicitud #{{ item.id }}
                                            </span>
                                            <span class="text-secondary text-xs">
                                                Plan {{ item.plan_id }} · {{ item.status }}
                                            </span>
                                        </a>
                                    }
                                }
                                @case ('admin_notifications') {
                                    @for (
                                        item of results.admin_notifications.items;
                                        track trackById($index, item)
                                    ) {
                                        <a
                                            class="hover:bg-hover flex flex-col gap-0.5 px-4 py-2"
                                            [routerLink]="['/admin/notifications']"
                                            [queryParams]="{ id: item.id }"
                                            (click)="closePanel()"
                                        >
                                            <span class="font-medium">{{ item.title }}</span>
                                        </a>
                                    }
                                }
                            }

                            @if (
                                results[group.key].total > results[group.key].items.length
                            ) {
                                <button
                                    class="text-primary hover:bg-hover px-4 py-2 text-left text-xs font-medium"
                                    (click)="goToFullResults(undefined, group.key)"
                                >
                                    +{{ results[group.key].total - results[group.key].items.length }}
                                    más
                                </button>
                            }
                        </div>
                    }
                }
            }
        </div>

        <!-- Footer: Ver todos -->
        @if (
            results
            && hasAnyResults()
            && searchControl.value
            && searchControl.value.toString().trim().length >= 2
        ) {
            <div class="flex flex-0 items-center justify-end border-t px-4 py-2">
                <button
                    mat-stroked-button
                    color="primary"
                    (click)="goToFullResults()"
                >
                    <mat-icon [svgIcon]="'heroicons_outline:arrow-right'"></mat-icon>
                    Ver todos los resultados
                </button>
            </div>
        }
    </div>
</ng-template>
